/**
 * Service worker registration with enhanced error handling and debugging
 * @typedef {Object} ServiceWorkerConfig
 * @property {(registration: ServiceWorkerRegistration) => void} [onUpdate] - Called when a new service worker is available
 * @property {(registration: ServiceWorkerRegistration) => void} [onSuccess] - Called when service worker registration is successful
 * @property {(error: Error) => void} [onError] - Called when service worker registration fails
 */

// Service worker registration with enhanced error handling and debugging

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/)
);

// Enable debug logging in development
const debug = process.env.NODE_ENV === 'development';

// Log helper function
const log = (...args) => {
  if (debug) {
    console.log('[Service Worker]', ...args);
  }
};

// Error helper function
const logError = (...args) => {
  console.error('[Service Worker]', ...args);
};

/**
 * Register the service worker
 * @param {ServiceWorkerConfig} [config] - Configuration object with callbacks
 */
export function register(config) {
  if (!('serviceWorker' in navigator)) {
    const error = new Error('Service workers are not supported in this browser');
    logError(error.message);
    if (config?.onError) {
      config.onError(error);
    }
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL || '/', window.location.href);
  
  // Check if we're on the same origin
  if (publicUrl.origin !== window.location.origin) {
    const error = new Error(`Service worker not registered - different origin: ${publicUrl.origin}`);
    logError(error.message);
    if (config?.onError) {
      config.onError(error);
    }
    return;
  }

  // Only register in production or on localhost in development
  if (process.env.NODE_ENV === 'production' || isLocalhost) {
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
    
    if (isLocalhost) {
      // Check if the service worker is available and valid on localhost
      checkValidServiceWorker(swUrl, config);
      
      // Log service worker readiness
      navigator.serviceWorker.ready
        .then(() => {
          log('Service worker is ready for offline use');
          if (config?.onSuccess) {
            config.onSuccess(navigator.serviceWorker.controller);
          }
        })
        .catch(error => {
          const err = new Error(`Service worker ready check failed: ${error.message}`);
          logError(err.message);
          if (config?.onError) {
            config.onError(err);
          }
        });
    } else {
      // In production, just register the service worker
      registerValidSW(swUrl, config);
    }
  } else {
    const message = 'Service worker not registered - not in production or localhost';
    log(message);
    if (config?.onError) {
      config.onError(new Error(message));
    }
  }
}

/**
 * Register a valid service worker
 * @param {string} swUrl - The service worker URL
 * @param {ServiceWorkerConfig} [config] - Configuration object with callbacks
 */
function registerValidSW(swUrl, config) {
  log(`Registering service worker at: ${swUrl}`);
  
  navigator.serviceWorker.register(swUrl)
    .then(registration => {
      log('Service worker registered successfully');
      
      // Set up the update found handler
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) {
          log('No installing worker found');
          return;
        }
        
        // Handle state changes of the installing worker
        installingWorker.onstatechange = () => {
          log(`Service worker state changed to: ${installingWorker.state}`);
          
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available
              log('New content is available; please refresh.');
              
              // Notify about update
              if (config?.onUpdate) {
                try {
                  config.onUpdate(registration);
                } catch (updateError) {
                  logError('Error in onUpdate callback:', updateError);
                }
              }
            } else {
              // Content is now cached for offline use
              log('Content is cached for offline use.');
              
              // Notify about successful registration
              if (config?.onSuccess) {
                try {
                  config.onSuccess(registration);
                } catch (successError) {
                  logError('Error in onSuccess callback:', successError);
                }
              }
            }
          }
        };
      };
      
      // Also check for any updates immediately
      registration.update().catch(updateError => {
        log('Failed to update service worker:', updateError);
      });
      
      return registration;
    })
    .catch(error => {
      const errorMessage = `Service worker registration failed: ${error.message}`;
      logError(errorMessage, error);
      
      // Notify about the error
      if (config?.onError) {
        config.onError(new Error(errorMessage));
      }
    });
}

/**
 * Check if the service worker at the given URL is valid
 * @param {string} swUrl - The service worker URL to check
 * @param {ServiceWorkerConfig} [config] - Configuration object with callbacks
 */
function checkValidServiceWorker(swUrl, config) {
  log(`Checking service worker validity at: ${swUrl}`);
  
  // Check if the service worker can be found
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
    cache: 'no-store',
    credentials: 'same-origin'
  })
    .then(response => {
      const contentType = response.headers.get('content-type');
      
      // Check if we got a valid service worker script
      if (response.status === 404 || 
          (contentType && !contentType.includes('javascript'))) {
        const errorMessage = `No valid service worker found at ${swUrl}. Status: ${response.status}, Content-Type: ${contentType}`;
        logError(errorMessage);
        
        // Unregister any existing service workers
        return navigator.serviceWorker.ready
          .then(registration => {
            log('Unregistering existing service worker...');
            return registration.unregister();
          })
          .then(() => {
            log('Service worker unregistered successfully');
            // Notify about the error before reloading
            if (config?.onError) {
              config.onError(new Error(errorMessage));
            }
            window.location.reload();
          });
      } else {
        log('Service worker is valid, proceeding with registration');
        registerValidSW(swUrl, config);
      }
    })
    .catch(error => {
      const errorMessage = `Error checking service worker at ${swUrl}: ${error.message}`;
      logError(errorMessage, error);
      log('App is running in offline mode');
      
      // Notify about the error
      if (config?.onError) {
        config.onError(new Error(errorMessage));
      }
      
      // Still try to register the service worker in case we're offline
      registerValidSW(swUrl, config);
    });
}

/**
 * Unregister the service worker and clear all caches
 * @returns {Promise<boolean>} A promise that resolves to true if unregistration was successful
 */
export function unregister() {
  return new Promise((resolve, reject) => {
    if (!('serviceWorker' in navigator)) {
      const message = 'Service workers are not supported in this browser';
      log(message);
      resolve(false);
      return;
    }

    log('Starting service worker unregistration...');
    
    // First, unregister the service worker
    navigator.serviceWorker.ready
      .then(registration => {
        log('Found active service worker, unregistering...');
        return registration.unregister();
      })
      .then(didUnregister => {
        if (didUnregister) {
          log('Service worker unregistered successfully');
        } else {
          log('No service worker was registered');
        }
        
        // Clear all caches
        log('Clearing all caches...');
        return caches.keys()
          .then(cacheNames => {
            if (cacheNames.length === 0) {
              log('No caches to delete');
              return [];
            }
            
            log(`Found ${cacheNames.length} caches to delete`);
            return Promise.all(
              cacheNames.map(cacheName => {
                log(`Deleting cache: ${cacheName}`);
                return caches.delete(cacheName)
                  .then(deleted => {
                    if (deleted) {
                      log(`Successfully deleted cache: ${cacheName}`);
                    } else {
                      log(`Cache ${cacheName} not found or could not be deleted`);
                    }
                    return deleted;
                  });
              })
            );
          })
          .then(() => didUnregister);
      })
      .then(didUnregister => {
        log('Cleanup completed successfully');
        
        // Force reload to ensure clean state
        if (typeof window !== 'undefined') {
          log('Reloading application...');
          window.location.reload();
        }
        
        resolve(didUnregister);
      })
      .catch(error => {
        const errorMessage = `Error during service worker unregistration: ${error.message}`;
        logError(errorMessage, error);
        reject(new Error(errorMessage));
      });
  });
}
